<template>
  <Teleport to="body">
    <Transition name="mobile-search-panel-fade">
      <div
        v-if="visible"
        class="mobile-search-panel-overlay"
        :style="{ paddingTop: `${headerHeight}px` }"
        @click="handleOverlayClick"
        @touchmove.prevent
      >
        <div
          class="mobile-search-panel-container mobile-search-panel-container--standard"
          @click.stop
          @touchmove.stop
        >
          <div class="mobile-search-panel__input-area">
            <div class="mobile-search-panel__input-wrapper">
              <button
                type="button"
                class="mobile-search-panel__back-btn"
                aria-label="返回"
                @click="handleCancel"
              >
                <icon-left />
              </button>
              <div class="mobile-search-panel__input-container">
                <a-input
                  ref="searchInputRef"
                  v-model="searchKeyword"
                  :placeholder="placeholder"
                  class="mobile-search-panel__input"
                  :maxlength="maxLength"
                  allow-clear
                  @focus="handleInputFocus"
                  @press-enter="handleSearchSubmit"
                >
                  <template #prefix>
                    <SvgIcon name="search" size="16" class="mobile-search-panel__search-icon" />
                  </template>
                </a-input>
              </div>
              <a-button type="text" class="mobile-search-panel__action-btn" @click="handleAction">
                {{ actionText }}
              </a-button>
            </div>
          </div>

          <div
            v-if="showSuggestionsSection"
            class="mobile-search-panel__section mobile-search-panel__section--suggestions"
          >
            <SearchSuggestionDropdown
              :keyword="searchKeyword"
              :items="suggestions"
              :loading="suggestionsLoading"
              :error="suggestionsError"
              :highlight-keyword="highlightSuggestions"
              variant="mobile"
              empty-text="暂无相关数据"
              show-empty-icon
              @select="handleSuggestionClick"
              @retry="emit('suggestion-retry')"
            />
          </div>

          <div
            v-if="!showSuggestionsSection && showHistory && searchHistoryList.length > 0"
            class="mobile-search-panel__section"
          >
            <div class="mobile-search-panel__section-title">
              <span>搜索历史</span>
              <IconDelete class="mobile-search-panel__clear-all" @click="emit('clear-history')" />
            </div>
            <div class="mobile-search-panel__tags">
              <base-search-tag
                v-for="(item, index) in searchHistoryList"
                :key="index"
                :text="truncateText(getHistoryText(item), 20)"
                @click="handleHistoryClick(item)"
              />
            </div>
          </div>

          <div
            v-if="showHotSearch && hotSearchLoading && hotSearchList.length === 0"
            class="mobile-search-panel__section"
          >
            <div class="mobile-search-panel__skeleton">
              <div class="mobile-search-panel__skeleton-title"></div>
              <div class="mobile-search-panel__skeleton-tags">
                <div v-for="i in 6" :key="i" class="mobile-search-panel__skeleton-tag"></div>
              </div>
            </div>
          </div>

          <div
            v-else-if="showHotSearch && hotSearchList.length > 0"
            class="mobile-search-panel__section"
          >
            <div class="mobile-search-panel__section-title">
              <span>热门搜索</span>
            </div>
            <div class="mobile-search-panel__tags">
              <base-search-tag
                v-for="(item, index) in hotSearchList"
                :key="index"
                :text="truncateText(getHotSearchText(item), 20)"
                @click="handleHotSearchClick(item)"
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/**
 * @description 移动端全屏搜索弹窗，只负责输入、候选项展示和交互事件
 * @param {boolean} visible - 控制显示/隐藏
 * @param {string} placeholder - 输入框占位符，由外部传入
 * @param {boolean} showHistory - 是否展示搜索历史
 * @param {string} initialKeyword - 打开时预填关键词
 * @emits update:visible - 关闭弹窗
 * @emits search - 提交搜索，传递关键词（可为空字符串）
 */

import { ref, computed, nextTick, watch } from 'vue'
import { IconDelete } from '@arco-design/web-vue/es/icon'
import { useDebounceFn } from '@/composables/useDebounceThrottle'
import SearchSuggestionDropdown from '~/components/base/SearchSuggestionDropdown.vue'
import { getBaseHeaderHeightByWidth } from '~/utils/theme'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '请输入关键词',
  },
  showHistory: {
    type: Boolean,
    default: true,
  },
  initialKeyword: {
    type: String,
    default: '',
  },
  enableSuggestions: {
    type: Boolean,
    default: false,
  },
  suggestions: {
    type: Array,
    default: () => [],
  },
  suggestionsLoading: {
    type: Boolean,
    default: false,
  },
  suggestionsError: {
    type: Boolean,
    default: false,
  },
  highlightSuggestions: {
    type: Boolean,
    default: true,
  },
  submitText: {
    type: String,
    default: '搜索',
  },
  cancelText: {
    type: String,
    default: '取消',
  },
  maxLength: {
    type: Number,
    default: 20,
  },
  historyItems: {
    type: Array,
    default: () => [],
  },
  hotSearchItems: {
    type: Array,
    default: () => [],
  },
  hotSearchLoading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'update:visible',
  'search',
  'focus',
  'suggestion-select',
  'suggestion-retry',
  'keyword-change',
  'clear-history',
])

const searchKeyword = ref('')
const headerHeight = ref(48)
const searchInputRef = ref(null)

const searchHistoryList = computed(() => props.historyItems)
const hotSearchList = computed(() => props.hotSearchItems)
const hotSearchLoading = computed(() => props.hotSearchLoading)
const showHotSearch = computed(() => props.hotSearchLoading || props.hotSearchItems.length > 0)
const showSuggestionsSection = computed(
  () => props.enableSuggestions && Boolean(searchKeyword.value.trim())
)
const isSearchKeywordEmpty = computed(() => !searchKeyword.value.trim())
const actionText = computed(() =>
  isSearchKeywordEmpty.value ? props.cancelText : props.submitText
)

function getHeaderHeight() {
  if (!import.meta.client) return
  try {
    const headerElement = document.querySelector('.base-header')
    if (headerElement) {
      headerHeight.value = headerElement.offsetHeight
    } else {
      headerHeight.value = getBaseHeaderHeightByWidth(window.innerWidth)
    }
  } catch (e) {
    headerHeight.value = 48
  }
}

function focusSearchInput() {
  if (!import.meta.client) return
  nextTick(() => {
    const inputEl =
      searchInputRef.value?.$el?.querySelector?.('input') ||
      searchInputRef.value?.inputRef ||
      searchInputRef.value?.input ||
      null

    if (inputEl && typeof inputEl.focus === 'function') {
      inputEl.focus()
    }
  })
}

function getHistoryText(item) {
  return getItemText(item)
}

function getHotSearchText(item) {
  return getItemText(item)
}

function getItemText(item) {
  if (typeof item === 'string' || typeof item === 'number') return String(item)
  return String(
    item?.displayText ??
      item?.label ??
      item?.text ??
      item?.title ??
      item?.name ??
      item?.keyword ??
      item?.content ??
      ''
  )
}

function truncateText(text, maxLength) {
  if (!text) return ''
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
}

function handleCancel() {
  emit('update:visible', false)
}

function handleOverlayClick() {
  emit('update:visible', false)
}

function doSearch(keyword) {
  emit('search', keyword == null ? '' : String(keyword).trim())
  emit('update:visible', false)
}

// 防抖：避免重复点击搜索/历史/热门导致多次跳转或重复请求
const debouncedDoSearch = useDebounceFn(doSearch, 300, {
  leading: true,
  trailing: false,
})

function handleSearchSubmit() {
  debouncedDoSearch(searchKeyword.value)
}

function handleInputFocus() {
  emit('focus', searchKeyword.value.trim())
}

function handleAction() {
  if (isSearchKeywordEmpty.value) {
    handleCancel()
    return
  }

  handleSearchSubmit()
}

function handleHistoryClick(item) {
  searchKeyword.value = getHistoryText(item)
  debouncedDoSearch(searchKeyword.value)
}

function handleHotSearchClick(item) {
  searchKeyword.value = getHotSearchText(item)
  debouncedDoSearch(searchKeyword.value)
}

function handleSuggestionClick(item) {
  emit('suggestion-select', item)
  emit('update:visible', false)
}

watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      getHeaderHeight()
      searchKeyword.value = props.initialKeyword || ''
      if (import.meta.client) {
        document.documentElement.style.overflow = 'hidden'
        document.body.style.overflow = 'hidden'
      }
      focusSearchInput()
    } else {
      if (import.meta.client) {
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
      }
    }
  },
  { immediate: true }
)

watch(searchKeyword, (value) => {
  emit('keyword-change', value ?? '')
})
</script>

<style lang="scss" scoped>
@use '~/styles/arco-input-mixins' as arco-input;

.mobile-search-panel-overlay {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba($text-title-color, 0.6);
  z-index: 999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
}

.mobile-search-panel-container {
  width: 100%;
  max-width: 100%;
  background-color: $bg-white;
  box-shadow: 0 4px 16px rgba($text-title-color, 0.1);
  overflow: hidden;
  animation: mobile-search-panel-slideDown 0.3s ease-out;
  box-sizing: border-box;
  padding: 0 16px 40px;
  height: 120%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.mobile-search-panel-container--standard {
  padding: 0 0 40px;

  .mobile-search-panel__input-area {
    padding: 12px 16px 0 12px;
  }

  .mobile-search-panel__input-wrapper {
    gap: 16px;
  }

  .mobile-search-panel__input-container {
    margin-right: 0;
  }

  .mobile-search-panel__action-btn {
    min-width: auto;
    height: 22px;
    padding: 0;
    color: $primary-color;
    font-size: 14px;
    line-height: 22px;
  }

  .mobile-search-panel__section--suggestions {
    padding: 0;
  }

  .mobile-search-panel__section:not(.mobile-search-panel__section--suggestions) {
    padding-top: 12px;
    padding-right: 16px;
    padding-left: 16px;
  }
}

.mobile-search-panel-fade-enter-active,
.mobile-search-panel-fade-leave-active {
  transition: opacity 0.3s ease;
}

.mobile-search-panel-fade-enter-from,
.mobile-search-panel-fade-leave-to {
  opacity: 0;
}

@keyframes mobile-search-panel-slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-search-panel__input-area {
  padding: 4px 0;
  box-sizing: border-box;
}

.mobile-search-panel__input-wrapper {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
}

.mobile-search-panel__input-container {
  flex: 1;
  margin-right: 8px;
  max-width: 100%;
}

.mobile-search-panel__back-btn {
  display: inline-flex;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $text-title-color;
  font-size: 20px;
  line-height: 1;
}

.mobile-search-panel__input {
  :deep(.arco-input) {
    height: 36px;
    font-size: 14px;
    color: $text-title-color;
  }

  :deep(.arco-input-wrapper) {
    height: 36px;
    border-radius: 6px;
    background-color: $bg-color;
    border: none;
    padding-left: 12px;
  }

  @include arco-input.show-clear-action-on-focus;

  :deep(.arco-input::placeholder) {
    color: $text-subtitle-color;
    font-size: 14px;
  }
}

.mobile-search-panel__search-icon {
  width: 18px;
  height: 18px;
  font-size: 18px;
  color: $text-content-color;
  flex-shrink: 0;
}

.mobile-search-panel__action-btn {
  height: 36px;
  min-width: 60px;
  padding: 0 16px;
  font-size: 14px;
  color: $text-content-color;
  border: none;
  background-color: transparent;
}

.mobile-search-panel__section {
  padding: 0 0 24px;

  &--suggestions {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding-top: 0;
  }
}

.mobile-search-panel__section-title {
  font-size: $font-size-card-subtitle;
  font-weight: 500;
  color: $text-title-color;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mobile-search-panel__clear-all {
  font-size: 16px;
  color: $text-content-color;
  cursor: pointer;
}

.mobile-search-panel__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-search-panel__skeleton-title {
  width: 80px;
  height: 24px;
  background: linear-gradient(90deg, $bg-color 25%, rgba($bg-color, 0.5) 50%, $bg-color 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  margin-bottom: 12px;
  animation: mobile-search-panel-skeleton 1.5s ease-in-out infinite;
}

.mobile-search-panel__skeleton-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-search-panel__skeleton-tag {
  width: 80px;
  height: 28px;
  background: linear-gradient(90deg, $bg-color 25%, rgba($bg-color, 0.5) 50%, $bg-color 75%);
  background-size: 200% 100%;
  border-radius: 32px;
  animation: mobile-search-panel-skeleton 1.5s ease-in-out infinite;
}

@keyframes mobile-search-panel-skeleton {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
